import { useRef, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '~/components/ui/dialog';
import { Input } from '~/components/ui/input';
import { Label } from '~/components/ui/label';
import { Button } from '~/components/ui/button';
import { useEnrollmentInvitePreview } from './useEnrollmentInvitePreview';
import { useInviteToEnroll } from './useInviteToEnroll';
import { inviteResultLabel } from './inviteWords';

interface BulkInviteDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  schoolId: string;
  familyEnrollmentIds: string[];
  onCompleted?: () => void;
}

interface Result {
  familyEnrollmentId: string;
  status: string;
  reason?: string | null;
}

// Invite the selected families. The preview counts who can be invited, lists
// the families already enrolling on the checklist, whom sending resends to, and
// says why the others can't; afterwards every selected family has a line: the
// server's answer for those sent, and "Not sent" with the reason for the rest.
export function BulkInviteDialog({ open, onOpenChange, schoolId, familyEnrollmentIds, onCompleted }: BulkInviteDialogProps) {
  const [deadlineOn, setDeadlineOn] = useState('');
  const [errors, setErrors] = useState<string[]>([]);
  const [results, setResults] = useState<Result[] | null>(null);
  const sending = useRef(false);
  const { data, loading: previewLoading, error: previewError } = useEnrollmentInvitePreview(
    open ? schoolId : undefined,
    familyEnrollmentIds
  );
  const [inviteToEnroll, { loading }] = useInviteToEnroll();

  const rows = data?.enrollmentInvitePreview ?? [];
  const invitable = rows.filter((row) => row.canInvite);
  const resending = invitable.filter((row) => row.willResend);
  const refused = rows.filter((row) => !row.canInvite);
  const summary = [
    `${invitable.length - resending.length} can be invited`,
    resending.length > 0 ? `${resending.length} will get a reminder` : null,
    refused.length > 0 ? `${refused.length} can't` : null,
  ].filter(Boolean).join(', ');
  const nameFor = (id: string) => rows.find((row) => row.familyEnrollmentId === id)?.familyName ?? 'Family';

  const handleOpenChange = (next: boolean) => {
    if (!next) {
      setDeadlineOn('');
      setErrors([]);
      setResults(null);
    }
    onOpenChange(next);
  };

  const handleSend = async () => {
    if (sending.current) return;
    sending.current = true;
    setErrors([]);
    try {
      const result = await inviteToEnroll({
        variables: {
          input: { familyEnrollmentIds: invitable.map((row) => row.familyEnrollmentId), deadlineOn: deadlineOn || null },
        },
      });
      const payload = result.data?.inviteToEnroll;
      if (payload?.errors && payload.errors.length > 0) {
        setErrors(payload.errors);
        return;
      }
      // Only the invitable families are sent, so the ones the preview refused
      // stay in the results as not sent, with the preview's reason.
      const notSent = refused.map((row) => ({ familyEnrollmentId: row.familyEnrollmentId, status: 'REFUSED', reason: row.reason }));
      setResults([...(payload?.results ?? []), ...notSent]);
      onCompleted?.();
    } catch (error) {
      setErrors([error instanceof Error ? error.message : 'The invitations were not sent. Please try again.']);
    } finally {
      sending.current = false;
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Invite to enroll</DialogTitle>
          <DialogDescription>
            {familyEnrollmentIds.length} {familyEnrollmentIds.length === 1 ? 'family' : 'families'} selected.
          </DialogDescription>
        </DialogHeader>

        {errors.length > 0 && (
          <div role="alert" className="space-y-1 text-sm text-destructive">
            {errors.map((message) => (
              <p key={message}>{message}</p>
            ))}
          </div>
        )}

        {previewLoading && <p className="text-sm text-muted-foreground">Checking who will be emailed…</p>}
        {previewError && <p role="alert" className="text-sm text-destructive">{previewError.message}</p>}

        {results ? (
          <ul className="divide-y rounded-md border" aria-label="Results">
            {results.map((result) => (
              <li key={result.familyEnrollmentId} className="flex flex-wrap items-center justify-between gap-x-4 gap-y-1 px-3 py-2 text-sm">
                <span>{nameFor(result.familyEnrollmentId)}</span>
                <span className={result.status === 'REFUSED' ? 'text-destructive' : 'text-muted-foreground'}>
                  {inviteResultLabel(result.status)}
                  {result.reason ? ` — ${result.reason}` : ''}
                </span>
              </li>
            ))}
          </ul>
        ) : (
          <>
            {data && <p className="text-sm">{summary}.</p>}
            {resending.length > 0 && (
              <ul className="divide-y rounded-md border" aria-label="Will get a reminder">
                {resending.map((row) => (
                  <li key={row.familyEnrollmentId} className="flex flex-wrap items-center justify-between gap-x-4 gap-y-1 px-3 py-2 text-sm">
                    <span>{row.familyName}</span>
                    <span className="text-muted-foreground">Will get a reminder</span>
                  </li>
                ))}
              </ul>
            )}
            {invitable.some((row) => row.warnings.length > 0) && (
              <ul className="divide-y rounded-md border border-amber-300 bg-amber-50 text-amber-900" aria-label="Worth a look before sending">
                {invitable.filter((row) => row.warnings.length > 0).map((row) => (
                  <li key={row.familyEnrollmentId} className="flex flex-wrap items-center justify-between gap-x-4 gap-y-1 px-3 py-2 text-sm">
                    <span>{row.familyName}</span>
                    <span>{row.warnings.join(' ')}</span>
                  </li>
                ))}
              </ul>
            )}
            {refused.length > 0 && (
              <ul className="divide-y rounded-md border" aria-label="Can't be invited">
                {refused.map((row) => (
                  <li key={row.familyEnrollmentId} className="flex flex-wrap items-center justify-between gap-x-4 gap-y-1 px-3 py-2 text-sm">
                    <span>{row.familyName}</span>
                    <span className="text-destructive">{row.reason ?? 'Can’t be invited'}</span>
                  </li>
                ))}
              </ul>
            )}
            <div className="space-y-1">
              <Label htmlFor="bulk-invite-deadline">Deadline (optional)</Label>
              <Input id="bulk-invite-deadline" type="date" value={deadlineOn} onChange={(e) => setDeadlineOn(e.target.value)} />
            </div>
          </>
        )}

        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => handleOpenChange(false)}>
            {results ? 'Done' : 'Cancel'}
          </Button>
          {!results && (
            <Button type="button" onClick={handleSend} disabled={loading || previewLoading || invitable.length === 0}>
              {loading ? 'Sending…' : `Send to ${invitable.length}`}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default BulkInviteDialog;
